// src/pages/EReceiptView.jsx
import { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import EBillReceipt from '../components/receipts/EBillReceipt'
import ApiService from '../services/api'
import AppURLs from '../config/urls'
import { Loader2, AlertCircle } from 'lucide-react'

const EReceiptView = () => {
  const { orderId } = useParams()
  const [order, setOrder] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    const fetchReceipt = async () => {
      try {
        setLoading(true)
        setError(null)
        
        // Use existing endpoint structure - GET /api/v1/orders/:id (public access, no auth)
        const endpoint = ApiService.replaceUrlParams(AppURLs.ORDERS.GET_BY_ID, { id: orderId })
        
        // Make fetch call without auth headers (public access)
        const response = await fetch(`${AppURLs.API}${endpoint}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        })
        
        // Check if response is JSON
        const contentType = response.headers.get('content-type')
        if (!contentType || !contentType.includes('application/json')) {
          throw new Error('Server returned non-JSON response. Order may not exist or endpoint requires authentication.')
        }
        
        const data = await response.json()
        
        if (response.ok && data.success) {
          // Extract order from nested response structure
          const orderData = data.data?.order || data.data
          setOrder(orderData)
        } else {
          setError(data.message || 'Receipt not found')
        }
      } catch (err) {
        console.error('Error fetching receipt:', err)
        if (err.message.includes('non-JSON response')) {
          setError('This order receipt is not available. The order may not exist or the link has expired.')
        } else {
          setError(err.message || 'Failed to load receipt. Please contact support.')
        }
      } finally {
        setLoading(false)
      }
    }

    if (orderId) {
      fetchReceipt()
    }
  }, [orderId])

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="h-12 w-12 text-orange-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-600 text-lg">Loading your receipt...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md text-center">
          <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Receipt Not Found</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <p className="text-sm text-gray-500">
            Please check the link or contact support if the problem persists.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-100 py-8 px-4 e-receipt-container">
      <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          .e-receipt-container,
          .e-receipt-container * {
            visibility: visible;
          }
          .e-receipt-container {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
          }
          .no-print {
            display: none !important;
          }
        }
      `}</style>
      
      <EBillReceipt order={order} />
    </div>
  )
}

export default EReceiptView
